/// <reference types="kendo-ui" />

/**
 * Concrete implementation of Hicom site search
 */
class HicomSiteSearch {
    private siteSearchToggle: HTMLButtonElement | null;
    private searchSubmit: HTMLButtonElement | null;
    private searchClear: HTMLElement | null;
    private searchResults: HTMLElement | null;
    private searchNoResults: HTMLElement | null;
    private searchKeywords: HTMLInputElement | null;
    private viewModel = kendo.observable({
        results: []
    });
    private _page: HicomPage;
    private _minChars = 3;

    /**
     * Initializes a new instance of the HicomSiteSearch class.
     * @param siteSearchId The ID of the site search element.
     */
    constructor(siteSearchId: string, hicomPage: HicomPage, minChars: number = 3) {
        this._page = hicomPage;
        this.siteSearchToggle = document.getElementsByClassName('siteSearch-toggle')[0] as HTMLButtonElement | null;
        this.searchSubmit = document.getElementsByClassName('siteSearch-submit')[0] as HTMLButtonElement | null;
        this.searchClear = document.getElementsByClassName('siteSearch-clear')[0] as HTMLElement | null;
        this.searchResults = document.getElementsByClassName('siteSearch-results')[0] as HTMLElement | null;
        this.searchNoResults = document.getElementsByClassName('siteSearch-noresults')[0] as HTMLElement | null;
        this.searchKeywords = document.getElementsByClassName('siteSearch-keywords')[0] as HTMLInputElement | null;
        this._minChars = minChars;

        this.addEventListeners();

        // Setup kendo binding
        if (this.searchResults != null) {
            kendo.bind(this.searchResults, this.viewModel);
        }
    }

    private debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => { func.apply(this, args); }, timeout);
        };
    }

    private formatPrettyUrl(url): string {
        const parts = url.split('//') as string[];
        return parts[0] + '//' + parts[1].replace(/\//g, ' › ');
    }

    private search(keywords): void {
        const encodedKeywords = encodeURIComponent(keywords)
        this._page.fetchGet(`${this._page.BaseUrl}/WebApi/Search?keywords=${encodedKeywords}`)
            .catch((error) => {
                console.error('SiteSearch::Error:', error);
            })
            .then((rawData: string | null | undefined) => {
                if (rawData) {
                    const data = JSON.parse(rawData);
                    const results = (data.Results || []).map(item => ({
                        ...item,
                        PrettyUrl: this.formatPrettyUrl(item.Url)
                    }));

                    this.viewModel.set("results", results);
                    this.afterResults(results.length > 0, keywords.length > 0);
                }
            })
    }

    private afterResults(hasResults, hasSearched) {
        this.toggle(this.searchNoResults, !hasResults && hasSearched)
        this.toggle(this.searchResults, hasResults && hasSearched)
    }

    private toggle(element: HTMLElement, visible: boolean) {
        element.style.display = visible ? 'block' : 'none';
    }

    private openWindow() {
        const $window = $('#site-search-window');
        if (!$window) return;
        const kendoWindow = $window.data('kendoWindow');
        if (kendoWindow) {
            if (kendoWindow.wrapper && !kendoWindow.wrapper.hasClass('siteSearch')) {
                kendoWindow.wrapper.addClass('siteSearch');
            }

            kendoWindow
                .open();

            this.searchKeywords.focus();
        }
    }

    private closeWindow() {
        const $window = $('#site-search-window');
        if (!$window) return;
        const kendoWindow = $window.data('kendoWindow');
        if (kendoWindow) {
            kendoWindow
                .close();
        }
    }

    /**
     * Adds event listeners to the site search element.
     */
    private addEventListeners(): void {

        // Ctrl + K, open window
        window.addEventListener('keydown', (e) => {
            if (e.ctrlKey && e.key === 'k') {
                e.preventDefault(); // Prevent browser default (e.g., browser search)
                this.openWindow();
            }
        });

        // Escape key, close window
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeWindow();
            }
        });

        // Button open window
        this.siteSearchToggle?.addEventListener('click', () => {
            this.openWindow();
        });

        const debouncedSearch = this.debounce(this.search, 300);

        this.searchKeywords.addEventListener('keyup', (event) => {
            const keywords = this.searchKeywords.value.trim();
            if (keywords.length < this._minChars) {
                this.searchSubmit.disabled = true

                this.viewModel.set("results", []);
                this.toggle(this.searchClear, false);
                this.afterResults(false, false);
            } else {
                this.searchSubmit.disabled = false
                if (event.key === 'Enter') {
                    debouncedSearch(keywords);
                    this.toggle(this.searchClear, true);
                }
            }
        });

        this.searchClear.addEventListener('click', () => {
            this.viewModel.set("results", []);
            this.searchKeywords.value = '';
            this.toggle(this.searchClear, false);
            this.afterResults(false, false)
        });

        this.searchSubmit.addEventListener('click', () => {
            const keywords = this.searchKeywords.value.trim();
            if (keywords.length >= this._minChars) {
                debouncedSearch(keywords);
                this.toggle(this.searchClear, true);
            }
        });
    }
}